// Minimal KML -> GeoJSON conversion for google.maps.Data. Google removed
// KmlLayer from the Maps JavaScript API in 3.66, so the coverage KML files
// are fetched and drawn client-side instead. Only what our KML files use is
// handled: Point / LineString / Polygon / MultiGeometry placemarks, styled
// through Style, StyleMap and gx:CascadingStyle.

export interface KmlStyle {
  fillColor?: string
  fillOpacity?: number
  strokeColor?: string
  strokeOpacity?: number
  strokeWeight?: number
  iconUrl?: string
  iconScale?: number
}

export interface KmlFeatureProperties {
  name: string
  description: string
  style: KmlStyle
}

type Position = [number, number]

type Geometry =
  | { type: 'Point', coordinates: Position }
  | { type: 'LineString', coordinates: Position[] }
  | { type: 'Polygon', coordinates: Position[][] }
  | { type: 'GeometryCollection', geometries: Geometry[] }

interface Feature {
  type: 'Feature'
  geometry: Geometry
  properties: KmlFeatureProperties
}

export interface FeatureCollection {
  type: 'FeatureCollection'
  features: Feature[]
}

const childrenByName = (parent: Element, name: string): Element[] =>
  Array.from(parent.children).filter((el) => el.localName === name)

const childByName = (parent: Element, name: string): Element | undefined =>
  childrenByName(parent, name)[0]

const childText = (parent: Element, name: string): string =>
  childByName(parent, name)?.textContent?.trim() ?? ''

// KML colors are aabbggrr hex
const parseKmlColor = (value: string): { color: string, opacity: number } | undefined => {
  const hex = value.trim().replace(/^#/, '')
  if (!/^[0-9a-f]{8}$/i.test(hex)) return undefined
  const [a, b, g, r] = [hex.slice(0, 2), hex.slice(2, 4), hex.slice(4, 6), hex.slice(6, 8)]
  return { color: `#${r}${g}${b}`, opacity: parseInt(a, 16) / 255 }
}

const parseStyle = (styleEl: Element): KmlStyle => {
  const style: KmlStyle = {}

  const poly = childByName(styleEl, 'PolyStyle')
  if (poly !== undefined) {
    const color = parseKmlColor(childText(poly, 'color'))
    if (color !== undefined) {
      style.fillColor = color.color
      style.fillOpacity = color.opacity
    }
    if (childText(poly, 'fill') === '0') style.fillOpacity = 0
    if (childText(poly, 'outline') === '0') style.strokeWeight = 0
  }

  const line = childByName(styleEl, 'LineStyle')
  if (line !== undefined) {
    const color = parseKmlColor(childText(line, 'color'))
    if (color !== undefined) {
      style.strokeColor = color.color
      style.strokeOpacity = color.opacity
    }
    const width = parseFloat(childText(line, 'width'))
    if (!Number.isNaN(width) && style.strokeWeight === undefined) style.strokeWeight = width
  }

  const icon = childByName(styleEl, 'IconStyle')
  if (icon !== undefined) {
    const iconEl = childByName(icon, 'Icon')
    const href = iconEl !== undefined ? childText(iconEl, 'href') : ''
    // Relative hrefs point inside a KMZ archive we don't have; fall back to the default marker
    if (/^https?:\/\//i.test(href)) style.iconUrl = href
    const scale = parseFloat(childText(icon, 'scale'))
    if (!Number.isNaN(scale)) style.iconScale = scale
  }

  return style
}

const parseCoordinates = (text: string): Position[] =>
  text.trim().split(/\s+/).flatMap((tuple) => {
    const [lng, lat] = tuple.split(',').map(Number)
    return Number.isFinite(lng) && Number.isFinite(lat) ? [[lng, lat] as Position] : []
  })

const ringCoordinates = (boundary: Element | undefined): Position[] => {
  const ring = boundary !== undefined ? childByName(boundary, 'LinearRing') : undefined
  return ring !== undefined ? parseCoordinates(childText(ring, 'coordinates')) : []
}

const parseGeometry = (el: Element): Geometry | null => {
  switch (el.localName) {
    case 'Point': {
      const [position] = parseCoordinates(childText(el, 'coordinates'))
      return position !== undefined ? { type: 'Point', coordinates: position } : null
    }
    case 'LineString': {
      const coordinates = parseCoordinates(childText(el, 'coordinates'))
      return coordinates.length > 1 ? { type: 'LineString', coordinates } : null
    }
    case 'Polygon': {
      const outer = ringCoordinates(childByName(el, 'outerBoundaryIs'))
      if (outer.length < 4) return null
      const inner = childrenByName(el, 'innerBoundaryIs').map(ringCoordinates).filter((r) => r.length >= 4)
      return { type: 'Polygon', coordinates: [outer, ...inner] }
    }
    case 'MultiGeometry': {
      const geometries = Array.from(el.children)
        .map(parseGeometry)
        .filter((g): g is Geometry => g !== null)
      return geometries.length > 0 ? { type: 'GeometryCollection', geometries } : null
    }
    default:
      return null
  }
}

const GEOMETRY_NAMES = new Set(['Point', 'LineString', 'Polygon', 'MultiGeometry'])

export const kmlToGeoJson = (kmlText: string): FeatureCollection => {
  const dom = new DOMParser().parseFromString(kmlText, 'application/xml')
  const all = Array.from(dom.getElementsByTagName('*'))

  // Shared styles, keyed by id (Style) or kml:id (gx:CascadingStyle)
  const styles = new Map<string, KmlStyle>()
  all.filter((el) => el.localName === 'Style' && el.getAttribute('id') !== null)
    .forEach((el) => { styles.set(el.getAttribute('id') as string, parseStyle(el)) })
  all.filter((el) => el.localName === 'CascadingStyle')
    .forEach((el) => {
      const id = el.getAttribute('kml:id') ?? el.getAttribute('id')
      const inner = childByName(el, 'Style')
      if (id !== null && inner !== undefined) styles.set(id, parseStyle(inner))
    })

  // StyleMap -> styleUrl of its "normal" pair
  const styleMaps = new Map<string, string>()
  all.filter((el) => el.localName === 'StyleMap' && el.getAttribute('id') !== null)
    .forEach((el) => {
      const normal = childrenByName(el, 'Pair').find((pair) => childText(pair, 'key') === 'normal')
      if (normal !== undefined) styleMaps.set(el.getAttribute('id') as string, childText(normal, 'styleUrl'))
    })

  const resolveStyleUrl = (styleUrl: string, depth = 0): KmlStyle => {
    if (depth > 5 || !styleUrl.startsWith('#')) return {}
    const id = styleUrl.slice(1)
    const mapped = styleMaps.get(id)
    if (mapped !== undefined) return resolveStyleUrl(mapped, depth + 1)
    return styles.get(id) ?? {}
  }

  const features: Feature[] = []
  all.filter((el) => el.localName === 'Placemark').forEach((placemark) => {
    const geometryEl = Array.from(placemark.children).find((el) => GEOMETRY_NAMES.has(el.localName))
    const geometry = geometryEl !== undefined ? parseGeometry(geometryEl) : null
    if (geometry === null) return

    const inlineStyle = childByName(placemark, 'Style')
    const style = {
      ...resolveStyleUrl(childText(placemark, 'styleUrl')),
      ...(inlineStyle !== undefined ? parseStyle(inlineStyle) : {})
    }

    features.push({
      type: 'Feature',
      geometry,
      properties: {
        name: childText(placemark, 'name'),
        description: childText(placemark, 'description'),
        style
      }
    })
  })

  return { type: 'FeatureCollection', features }
}

// Descriptions are authored HTML; show them as plain text to keep markup out of the page
export const descriptionToText = (html: string): string => {
  const withBreaks = html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/(div|p|li)>/gi, '\n')
  const text = new DOMParser().parseFromString(withBreaks, 'text/html').body.textContent ?? ''
  return text.replace(/\n{2,}/g, '\n').trim()
}
